import React, { useState, useEffect } from 'react'
import { Modal } from 'react-bootstrap'
import { Formik, Form } from 'formik'
import { useParams } from 'react-router'
import {
  useErrorService,
  useNotificationService,
  Button,
} from '@abroad/components'
import {
  ExperienceCheckbox,
  ExperienceDatePicker,
  ExperienceSelect,
} from '../form'
import CurrencyInput from '../enterpriseSOW/CurrencyInput'
import API from '../../utils/API'
import * as Yup from 'yup'
import moment from 'moment-timezone'
import classNames from 'classnames'
import { UTCFormatDate, getCurrentDate } from '../../utils/date'
import { getInquiryEnrollPrefill } from './inquiryContactUtils'
import {
  calculateDepositAmount as calculateQuestDepositAmount,
  getAutoDeductDays,
  getDefaultAutoDeductDateValue,
  getDepositPercentage,
  isDiscountProvided,
  stripCurrencyValue,
} from '../../utils/questPayment'

const AUTO_DEDUCT_DATE_FORMAT = 'MM/DD/YYYY'

const capitalizeOccupancyFromRaw = (raw) => {
  if (raw == null || typeof raw !== 'string') return null
  const o = raw.trim().toLowerCase()
  if (o !== 'single' && o !== 'double') return null
  return o.charAt(0).toUpperCase() + o.slice(1)
}

const resolveInviteOccupancy = (userData, participantData) => {
  if (participantData?.accommodation?.occupancy) {
    const cap = capitalizeOccupancyFromRaw(
      participantData.accommodation.occupancy,
    )
    return cap || 'Double'
  }
  if (
    userData &&
    !userData._id &&
    userData.readyToEnroll &&
    userData.occupancyPreference != null &&
    String(userData.occupancyPreference).trim() !== ''
  ) {
    const cap = capitalizeOccupancyFromRaw(userData.occupancyPreference)
    if (cap) return cap
  }
  return 'Double'
}

const parseInvestmentNumber = (investments, questDetails, key) => {
  const v = investments?.[key] ?? questDetails?.investments?.[key]
  if (v === undefined || v === null || v === '') return 0
  const n = Number(String(v).replace(/[^0-9.]/g, ''))
  return Number.isFinite(n) ? n : 0
}

const getOccupancyRate = (occupancy, investments, questDetails) => {
  const standardRate = parseInvestmentNumber(
    investments,
    questDetails,
    'standardRate',
  )
  const singleRate = parseInvestmentNumber(
    investments,
    questDetails,
    'singleSupplement',
  )
  const resolved =
    capitalizeOccupancyFromRaw(occupancy) || occupancy || 'Double'
  if (resolved === 'Single') {
    return (
      investments?.singleSupplement ??
      questDetails?.investments?.singleSupplement ??
      singleRate
    )
  }
  return (
    investments?.standardRate ??
    questDetails?.investments?.standardRate ??
    standardRate
  )
}

const getInitialCostActual = (
  userData,
  participantData,
  questDetails,
  investments,
) => {
  // Prefer manually saved actual cost from API over occupancy default rates
  const savedActualCost =
    participantData?.pricing?.actualCost ??
    participantData?.paymentSummary?.actualCost
  if (
    savedActualCost !== undefined &&
    savedActualCost !== null &&
    savedActualCost !== ''
  ) {
    return savedActualCost
  }

  const resolved = resolveInviteOccupancy(userData, participantData)
  return getOccupancyRate(resolved, investments, questDetails)
}

/** Saved participant date wins (null = with the deposit); otherwise the experience default. */
const getInitialAutoDeductDate = (
  participantData,
  questDetails,
  investments,
) => {
  const savedDate = participantData?.paymentAutomation?.autoDeductDate
  if (savedDate !== undefined) {
    return savedDate ? UTCFormatDate(savedDate) : ''
  }
  return getDefaultAutoDeductDateValue(questDetails, investments)
}

const getAutoDeductHint = (defaultDays) => {
  if (!defaultDays) {
    return 'Default is with the deposit for this experience. Pick a date if the remaining balance should be charged later. If they pay by card, it is charged automatically on that date.'
  }
  return `Derived from the default of ${defaultDays} days before departure set for this experience. If they pay by card, the remaining balance is charged automatically on this date.`
}

const formatUsdLabel = (value) => {
  const n = Number(String(value ?? '').replace(/[^0-9.]/g, ''))
  if (!Number.isFinite(n) || n <= 0) return ''
  return `US$${n.toLocaleString('en-US')}`
}

const UserDetailsModal = ({
  showModal,
  handleClose,
  onBack,
  userData,
  participantData,
  updateParticipants,
  questDetails,
  investments,
  enrollFromInquiries = false,
}) => {
  const [occupancy, setOccupancy] = useState(() =>
    resolveInviteOccupancy(userData, participantData),
  )
  const [isFormSubmitting, setIsFormSubmitting] = useState(false)
  const [singleSupplement, setSingleSupplement] = useState(false)
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [firstNameTouched, setFirstNameTouched] = useState(false)
  const [lastNameTouched, setLastNameTouched] = useState(false)
  // Once the admin types a deposit, price/occupancy/discount changes stop recalculating it
  const [depositTouched, setDepositTouched] = useState(false)
  const Error = useErrorService()
  const questInvestments = investments ?? questDetails?.investments
  const defaultAutoDeductDays = getAutoDeductDays(questInvestments)

  const calculateDepositAmount = (actualCost, discountedCost, currentDeposit) =>
    depositTouched
      ? currentDeposit
      : calculateQuestDepositAmount(
          actualCost,
          discountedCost,
          getDepositPercentage(questInvestments),
        )
  const { questId } = useParams()
  const Notification = useNotificationService()

  const validationSchema = Yup.object().shape({
    costActual: Yup.string()
      .nullable()
      .transform((value, originalValue) => (originalValue == null ? '' : value))
      .required('Actual amount is required'),
    costDeposit: Yup.string()
      .test(
        'deposit-lt-actual',
        'Deposit amount must be less than actual cost',
        function (value) {
          const { costActual } = this.parent
          const depositStr = value || ''
          const actualStr = costActual || ''
          if (!depositStr || !actualStr) return true
          const depositValue = Number(depositStr.replace(/[^0-9.]/g, ''))
          const actualValue = Number(actualStr.replace(/[^0-9.]/g, ''))
          return depositValue <= actualValue
        },
      )
      .test(
        'deposit-lt-eq-discount',
        'Deposit amount must be less than or equal to discount amount',
        function (value) {
          const { costDiscount, applyDiscount } = this.parent
          if (
            !applyDiscount ||
            !isDiscountProvided(value) ||
            !isDiscountProvided(costDiscount)
          ) {
            return true
          }
          const depositValue = stripCurrencyValue(value)
          const discountValue = stripCurrencyValue(costDiscount)
          return depositValue <= discountValue
        },
      ),
    costDiscount: Yup.string()
      .test(
        'discount-lt-actual',
        'Discount amount must be less than actual cost',
        function (value) {
          const { costActual, applyDiscount } = this.parent
          if (
            !applyDiscount ||
            !isDiscountProvided(value) ||
            !isDiscountProvided(costActual)
          ) {
            return true
          }
          const discountValue = stripCurrencyValue(value)
          const actualValue = stripCurrencyValue(costActual)
          return discountValue <= actualValue
        },
      )
      .test(
        'discount-non-negative',
        'Discount amount cannot be negative',
        function (value) {
          if (!this.parent.applyDiscount || !isDiscountProvided(value)) {
            return true
          }
          return stripCurrencyValue(value) >= 0
        },
      ),
    autoDeductDate: Yup.string()
      .nullable()
      .test(
        'auto-deduct-date-required',
        'Remaining balance due date is required',
        // Only required when the experience has a default date to derive from
        (value) =>
          !!value || !getDefaultAutoDeductDateValue(questDetails, investments),
      )
      .test(
        'auto-deduct-date-valid',
        'Enter a valid date',
        (value) =>
          !value || moment(value, AUTO_DEDUCT_DATE_FORMAT, true).isValid(),
      ),
  })

  useEffect(() => {
    if (enrollFromInquiries && userData && !userData._id) {
      const { firstName: inquiryFirstName, lastName: inquiryLastName } =
        getInquiryEnrollPrefill(userData)
      setFirstName(inquiryFirstName || '')
      setLastName(inquiryLastName || '')
    } else if (userData && !userData._id) {
      setFirstName(userData.firstName || '')
      setLastName(userData.lastName || '')
    } else if (userData && userData._id) {
      if (userData?.userId?.fullName) {
        const nameParts = userData.userId.fullName.trim().split(/\s+/)
        if (nameParts.length === 2) {
          setFirstName(nameParts[0])
          setLastName(nameParts[1])
        } else if (nameParts.length === 4) {
          setFirstName(nameParts.slice(0, 2).join(' '))
          setLastName(nameParts.slice(2).join(' '))
        } else {
          setFirstName(nameParts[0] || '')
          setLastName('')
        }
      } else {
        setFirstName(userData.firstName || '')
        setLastName(userData.lastName || '')
      }
    }

    const resolvedOccupancy = resolveInviteOccupancy(userData, participantData)
    setOccupancy(resolvedOccupancy)

    if (participantData?.accommodation?.singleSupplement !== undefined) {
      setSingleSupplement(participantData.accommodation.singleSupplement)
    } else {
      setSingleSupplement(resolvedOccupancy === 'Single')
    }

    // Reset touched states when userData or participantData changes
    setFirstNameTouched(false)
    setLastNameTouched(false)
    setDepositTouched(false)
  }, [userData, participantData, enrollFromInquiries])

  const stripCurrency = (val) => {
    if (!val) return ''
    return stripCurrencyValue(val)
  }

  const handleAssignUser = async (values) => {
    setIsFormSubmitting(true)
    const accommodation = {
      occupancy: occupancy.toLowerCase(),
      singleSupplement,
    }
    const applyDiscount = !!values.applyDiscount
    const pricing = {
      actualCost: stripCurrency(values.costActual) || 0,
      discountedCost:
        applyDiscount && isDiscountProvided(values.costDiscount)
          ? stripCurrencyValue(values.costDiscount)
          : null,
      discountReason: applyDiscount ? values.costDiscountReason || '' : '',
      depositAmount: isDiscountProvided(values.costDeposit)
        ? stripCurrencyValue(values.costDeposit)
        : 0,
      // null = collect the remaining balance with the deposit
      autoDeductDate: values.autoDeductDate
        ? moment
            .utc(values.autoDeductDate, AUTO_DEDUCT_DATE_FORMAT)
            .format('YYYY-MM-DD')
        : null,
    }
    try {
      if (userData?._id) {
        if (participantData?.paymentSummary?.canPayFull) {
          await API.AdminAPI.quests.updateSingleParticipant(
            questId,
            userData.userId._id,
            {
              accommodation,
              pricing,
            },
          )
          Notification.showNotification(
            'User has been updated successfully',
            'success',
          )
        } else {
          await API.AdminAPI.quests.addUserQuest(questId, {
            userId: userData._id,
            accommodation,
            pricing,
          })
          Notification.showNotification(
            'User has been added successfully',
            'success',
          )
        }
      } else {
        const inviteEmail =
          userData?.email || participantData?.userId?.email || ''

        if (enrollFromInquiries) {
          if (!userData?.inquiryId) {
            Error.showError({
              message:
                'Missing inquiry or form question data. Cannot complete enrollment.',
            })
            setIsFormSubmitting(false)
            return
          }
          await API.AdminAPI.quests.enrollInquiry(questId, userData.inquiryId, {
            email: inviteEmail,
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            accommodation: {
              occupancy: accommodation.occupancy,
              singleSupplement: String(!!singleSupplement),
            },
            pricing: {
              actualCost: pricing.actualCost,
              discountedCost: pricing.discountedCost,
              discountReason: pricing.discountReason || '',
              depositAmount: pricing.depositAmount,
              autoDeductDate: pricing.autoDeductDate,
            },
          })
          Notification.showNotification(
            'User has been enrolled successfully',
            'success',
          )
        } else {
          await API.AdminAPI.quests.addUserQuest(questId, {
            firstName,
            lastName,
            email: inviteEmail,
            accommodation,
            pricing,
          })
          Notification.showNotification(
            'User has been added successfully',
            'success',
          )
        }
      }
      setIsFormSubmitting(false)
      handleClose()
      updateParticipants()
    } catch (e) {
      Error.showError(e)
      setIsFormSubmitting(false)
    }
  }

  if (!userData && !participantData) return null

  const emailValue = userData?.email || participantData?.userId?.email || ''
  const isUpdate = !!(
    userData?._id && participantData?.paymentSummary?.canPayFull
  )
  const isExistingAccount = !!(userData?._id && !isUpdate)
  const inviteTitle = isUpdate
    ? emailValue
      ? `Update ${emailValue}`
      : 'Update invite'
    : emailValue
    ? `Invite ${emailValue}`
    : 'Invite'
  const chipLabel = isUpdate
    ? 'Update invite'
    : isExistingAccount || (userData?._id && !enrollFromInquiries)
    ? 'Existing account'
    : 'New invite'
  const chipClass =
    isExistingAccount || (userData?._id && !isUpdate)
      ? 'badge-experience badge-live'
      : 'badge-experience badge-soon'
  const doubleRate = formatUsdLabel(
    investments?.standardRate ?? questDetails?.investments?.standardRate,
  )
  const singleRate = formatUsdLabel(
    investments?.singleSupplement ??
      questDetails?.investments?.singleSupplement,
  )
  const showBack =
    typeof onBack === 'function' && !enrollFromInquiries && !isUpdate

  return (
    <Modal
      show={showModal}
      onHide={handleClose}
      centered
      dialogClassName='experience-add-part-dialog'
      contentClassName='experience-add-part-card is-scroll'>
      <Modal.Body className='p-0'>
        <div className='experience-add-part'>
          <h2>{inviteTitle}</h2>
          <p className='experience-add-part-lead'>
            Set occupancy and price for this person.
          </p>
          <div className='experience-add-part-chip-row'>
            <span className={chipClass}>{chipLabel}</span>
          </div>
          <Formik
            initialValues={{
              costActual: String(
                getInitialCostActual(
                  userData,
                  participantData,
                  questDetails,
                  investments,
                ) ?? '',
              ),
              costDeposit: String(
                (() => {
                  const savedDeposit =
                    participantData?.pricing?.depositAmount ??
                    participantData?.paymentSummary?.depositAmount
                  if (savedDeposit !== undefined && savedDeposit !== null) {
                    return savedDeposit
                  }
                  const savedDiscount =
                    participantData?.pricing?.discountedCost ??
                    participantData?.paymentSummary?.discountedCost
                  return calculateQuestDepositAmount(
                    getInitialCostActual(
                      userData,
                      participantData,
                      questDetails,
                      investments,
                    ),
                    savedDiscount ?? '',
                    getDepositPercentage(questInvestments),
                  )
                })(),
              ),
              costDiscount: String(
                (() => {
                  const savedDiscount =
                    participantData?.pricing?.discountedCost ??
                    participantData?.paymentSummary?.discountedCost
                  return savedDiscount !== undefined && savedDiscount !== null
                    ? savedDiscount
                    : ''
                })(),
              ),
              costDiscountReason:
                participantData?.pricing?.discountReason || '',
              applyDiscount: (() => {
                const savedDiscount =
                  participantData?.pricing?.discountedCost ??
                  participantData?.paymentSummary?.discountedCost
                return savedDiscount !== undefined && savedDiscount !== null
              })(),
              autoDeductDate: getInitialAutoDeductDate(
                participantData,
                questDetails,
                investments,
              ),
            }}
            enableReinitialize
            validationSchema={validationSchema}
            onSubmit={handleAssignUser}>
            {({
              handleSubmit,
              errors,
              touched,
              values,
              handleChange,
              handleBlur,
              setFieldValue,
              setFieldTouched,
              setValues,
              validateForm,
            }) => {
              const disableAll =
                userData?._id &&
                participantData?.paymentSummary?.canPayFull === false
              const nameReadOnly = disableAll || !!userData?._id
              const showNameError =
                !userData?._id &&
                firstNameTouched &&
                lastNameTouched &&
                (!firstName.trim() || !lastName.trim())

              return (
                <Form onSubmit={handleSubmit}>
                  <div className='experience-add-part-fields'>
                    <div className='experience-f'>
                      <label className='experience-f-label'>Email</label>
                      <input
                        className='form-control experience-f-control'
                        type='email'
                        value={emailValue}
                        readOnly
                        disabled={
                          disableAll || !!userData?._id || enrollFromInquiries
                        }
                        name='email'
                      />
                    </div>

                    {isUpdate ? (
                      <div className='experience-f'>
                        <label className='experience-f-label'>Full name</label>
                        <input
                          className='form-control experience-f-control'
                          type='text'
                          value={
                            userData?.userId?.fullName ||
                            [userData?.firstName, userData?.lastName]
                              .filter(Boolean)
                              .join(' ')
                          }
                          disabled
                          name='fullName'
                        />
                      </div>
                    ) : (
                      <>
                        <div className='experience-add-part-name-row'>
                          <div className='experience-f'>
                            <label className='experience-f-label'>
                              First name
                            </label>
                            <input
                              className='form-control experience-f-control'
                              type='text'
                              value={
                                userData?._id
                                  ? userData?.userId?.fullName
                                    ? firstName
                                    : userData?.firstName
                                  : firstName
                              }
                              onChange={(e) => {
                                if (!userData?._id) setFirstName(e.target.value)
                              }}
                              onBlur={() => setFirstNameTouched(true)}
                              disabled={nameReadOnly}
                              name='firstName'
                              autoComplete='off'
                            />
                          </div>
                          <div className='experience-f'>
                            <label className='experience-f-label'>
                              Last name
                            </label>
                            <input
                              className='form-control experience-f-control'
                              type='text'
                              value={
                                userData?._id
                                  ? userData?.userId?.fullName
                                    ? lastName
                                    : userData?.lastName
                                  : lastName
                              }
                              onChange={(e) => {
                                if (!userData?._id) setLastName(e.target.value)
                              }}
                              onBlur={() => setLastNameTouched(true)}
                              disabled={nameReadOnly}
                              name='lastName'
                              autoComplete='off'
                            />
                          </div>
                        </div>
                        {showNameError ? (
                          <p className='experience-add-part-error'>
                            First and last name are required for a new invite.
                          </p>
                        ) : null}
                      </>
                    )}

                    <ExperienceSelect
                      id='inviteOccupancy'
                      label='Occupancy'
                      value={occupancy}
                      options={[
                        {
                          value: 'Double',
                          label: doubleRate
                            ? `Double · ${doubleRate}`
                            : 'Double',
                        },
                        {
                          value: 'Single',
                          label: singleRate
                            ? `Single · ${singleRate}`
                            : 'Single',
                        },
                      ]}
                      isSearchable={false}
                      disabled={disableAll}
                      onChange={(e) => {
                        const selected = e.target.value
                        setOccupancy(selected)
                        setSingleSupplement(selected === 'Single')

                        const newCost = getOccupancyRate(
                          selected,
                          investments,
                          questDetails,
                        )

                        setValues({
                          ...values,
                          costActual: String(newCost),
                          costDeposit: calculateDepositAmount(
                            newCost,
                            values.applyDiscount ? values.costDiscount : '',
                            values.costDeposit,
                          ),
                        })

                        setTimeout(() => {
                          setFieldTouched('costActual', true, false)
                          setFieldTouched('costDiscount', true, false)
                          setFieldTouched('costDeposit', true, false)
                          validateForm().catch(console.error)
                        }, 0)
                      }}
                    />

                    <div className='experience-f'>
                      <label className='experience-f-label'>Price</label>
                      <CurrencyInput
                        inputClasses='form-control experience-f-control'
                        name='costActual'
                        value={values.costActual}
                        onChange={(e) => {
                          const nextActual = e.target.value
                          setValues({
                            ...values,
                            costActual: nextActual,
                            costDeposit: calculateDepositAmount(
                              nextActual,
                              values.applyDiscount ? values.costDiscount : '',
                              values.costDeposit,
                            ),
                          })
                        }}
                        onBlur={handleBlur}
                        disabled={disableAll}
                      />
                      {touched.costActual && errors.costActual ? (
                        <p className='experience-add-part-error'>
                          {errors.costActual}
                        </p>
                      ) : null}
                    </div>

                    <ExperienceCheckbox
                      name='applyDiscount'
                      label='Apply discount'
                      checked={!!values.applyDiscount}
                      disabled={disableAll}
                      onChange={(e) => {
                        const checked = e.target.checked
                        if (checked) {
                          setFieldValue('applyDiscount', true)
                        } else {
                          setValues({
                            ...values,
                            applyDiscount: false,
                            costDiscount: '',
                            costDiscountReason: '',
                            costDeposit: calculateDepositAmount(
                              values.costActual,
                              '',
                              values.costDeposit,
                            ),
                          })
                        }
                      }}
                    />

                    {values.applyDiscount ? (
                      <div className='experience-discount-fields'>
                        <div className='experience-f'>
                          <label className='experience-f-label'>
                            Discounted price
                          </label>
                          <CurrencyInput
                            inputClasses='form-control experience-f-control'
                            name='costDiscount'
                            onChange={(e) => {
                              const nextDiscount = e.target.value
                              setValues({
                                ...values,
                                costDiscount: nextDiscount,
                                costDeposit: calculateDepositAmount(
                                  values.costActual,
                                  nextDiscount,
                                  values.costDeposit,
                                ),
                              })
                            }}
                            disabled={disableAll}
                          />
                          <p className='experience-f-hint text-dark'>
                            Amount they will pay after the discount.
                          </p>
                          {touched.costDiscount && errors.costDiscount ? (
                            <p className='experience-add-part-error'>
                              {errors.costDiscount}
                            </p>
                          ) : null}
                        </div>
                        <div className='experience-f'>
                          <label className='experience-f-label'>
                            Discount reason
                          </label>
                          <input
                            className='form-control experience-f-control'
                            type='text'
                            name='costDiscountReason'
                            value={values.costDiscountReason || ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            disabled={disableAll}
                            placeholder='Optional'
                          />
                        </div>
                      </div>
                    ) : null}

                    <div className='experience-f'>
                      <label className='experience-f-label'>Deposit</label>
                      <CurrencyInput
                        inputClasses='form-control experience-f-control'
                        name='costDeposit'
                        value={values.costDeposit}
                        onChange={(e) => {
                          setDepositTouched(true)
                          setFieldValue('costDeposit', e.target.value)
                        }}
                        onBlur={handleBlur}
                        disabled={disableAll}
                      />
                      {touched.costDeposit && errors.costDeposit ? (
                        <p className='experience-add-part-error'>
                          {errors.costDeposit}
                        </p>
                      ) : null}
                    </div>

                    <div className='experience-f'>
                      <ExperienceDatePicker
                        name='autoDeductDate'
                        label='Remaining balance due'
                        labelProps={{ className: 'experience-f-label' }}
                        placeholder='Add date'
                        minimumDate={getCurrentDate()}
                        className='experience-f-control'
                        disabled={disableAll}
                        displayFlex={false}
                        position='auto'
                      />
                      <p className='experience-f-hint text-dark'>
                        {getAutoDeductHint(defaultAutoDeductDays)}
                      </p>
                      {touched.autoDeductDate && errors.autoDeductDate ? (
                        <p className='experience-add-part-error'>
                          {errors.autoDeductDate}
                        </p>
                      ) : null}
                    </div>
                  </div>

                  <p
                    className={classNames('experience-invite-note', {
                      'is-new-user': !userData?._id,
                      'is-existing-user': !!userData?._id,
                    })}>
                    They will receive an email with a link to enroll.
                  </p>

                  <div className='experience-modal-actions'>
                    {showBack ? (
                      <button
                        type='button'
                        className='btn-outline-experience'
                        onClick={onBack}
                        disabled={isFormSubmitting}>
                        Back
                      </button>
                    ) : (
                      <button
                        type='button'
                        className='btn-outline-experience'
                        onClick={handleClose}
                        disabled={isFormSubmitting}>
                        Cancel
                      </button>
                    )}
                    {isUpdate ? (
                      <Button
                        type='submit'
                        variant='saffron'
                        size='sm'
                        disabled={
                          !values.costActual ||
                          !!errors.costActual ||
                          !!errors.costDeposit ||
                          !!errors.costDiscount ||
                          !!errors.autoDeductDate ||
                          isFormSubmitting
                        }
                        isLoading={isFormSubmitting}
                        className='btn-navy border-0'>
                        <span>Update</span>
                      </Button>
                    ) : null}
                    {(!userData?._id ||
                      participantData?.paymentSummary?.canPayFull ===
                        undefined) && (
                      <Button
                        type='submit'
                        variant='saffron'
                        size='sm'
                        disabled={
                          !values.costActual ||
                          !!errors.costActual ||
                          !!errors.costDeposit ||
                          !!errors.costDiscount ||
                          !!errors.autoDeductDate ||
                          !firstName.trim() ||
                          !lastName.trim() ||
                          isFormSubmitting
                        }
                        isLoading={isFormSubmitting}
                        className='btn-navy border-0'>
                        <span>
                          {enrollFromInquiries ? 'Enroll' : 'Send invite'}
                        </span>
                      </Button>
                    )}
                  </div>
                </Form>
              )
            }}
          </Formik>
        </div>
      </Modal.Body>
    </Modal>
  )
}

export default UserDetailsModal
